import {
  Badge,
  BrandLoader,
  Col,
  Flex,
  Modal,
  Row,
  TabsFactory,
  Text,
  Title,
  Tooltip,
} from '@wyz/ui';
import {
  BlobHelpers,
  DEFAULT_TIRE_BRAND_LOGO,
  DEFAULT_TIRE_BRAND_WIDTH,
  getBrandLogoUrl,
} from '@wyz/utils';
import { useEffect, useState } from 'react';
import {
  Trans,
  useApplicationContext,
  useConfig,
  useI18n,
} from '~/common/context';
import { TranslationKeys } from '~/common/types';
import { ImageSlide, Img, SeasonIcon } from '~/purchase/components';
import {
  DEFAULT_ENERGY_LABEL,
  getFilterTranslation,
  WcwSearchHelpers,
} from '~/purchase/helpers';
import {
  IWcwCompatibleVehicle,
  IWcwOffer,
  IWcwTyre,
  IWcwWheel,
} from '~/purchase/types';

type WcwDetailTab = 'wheel' | 'vehicle' | 'tyre';

const Tabs = TabsFactory<WcwDetailTab>();

type Props = {
  onClose: () => void;
  offer: IWcwOffer;
};

function WheelRow({
  labelKey,
  value,
}: {
  labelKey: keyof TranslationKeys;
  value: React.ReactNode;
}) {
  if (!value) return null;
  return (
    <tr>
      <td>
        <Text weight='semi-bold' color='secondary-gray' heading='xxs'>
          <Trans i18nKey={labelKey} />
        </Text>
      </td>
      <td>
        <Text weight='semi-bold' color='primary' heading='xxs'>
          {value}
        </Text>
      </td>
    </tr>
  );
}

function WheelInformationTabContent({ wheel }: { wheel: IWcwWheel }) {
  return (
    <table className='table table-striped mt-3'>
      <tbody>
        <WheelRow
          labelKey='app.purchase.wcw.detail.wheel.ref'
          value={wheel.reference}
        />
        <WheelRow
          labelKey='app.purchase.wcw.detail.wheel.rim_reference'
          value={wheel.rimReference}
        />
        <WheelRow
          labelKey='app.purchase.wcw.detail.wheel.tmps_ref'
          value={wheel.parts.tpmsRef}
        />
        <WheelRow
          labelKey='app.purchase.wcw.detail.wheel.hubcap_ref'
          value={wheel.parts.hubcapRef}
        />
        <WheelRow
          labelKey='app.purchase.wcw.detail.wheel.rim_diameter'
          value={wheel.rimDiameter}
        />
        <WheelRow labelKey='app.common.wheel.rim_type' value={wheel.rimType} />
      </tbody>
    </table>
  );
}

function VehicleInformationTabContent({
  compatibleVehicles,
}: {
  compatibleVehicles: IWcwCompatibleVehicle[];
}) {
  const { t } = useI18n();
  return (
    <table className='table table-striped mt-3'>
      <thead>
        <tr>
          <th>
            <Text weight='semi-bold' color='secondary-gray' heading='xxs'>
              <Trans i18nKey='app.purchase.wcw.detail.vehicle.brand' />
            </Text>
          </th>
          <th>
            <Text weight='semi-bold' color='secondary-gray' heading='xxs'>
              <Trans i18nKey='app.purchase.wcw.detail.vehicle.model' />
            </Text>
          </th>
          <th>
            <Text weight='semi-bold' color='secondary-gray' heading='xxs'>
              <Trans i18nKey='app.purchase.wcw.detail.vehicle.version' />
            </Text>
          </th>
          <th>
            <Text weight='semi-bold' color='secondary-gray' heading='xxs'>
              <Trans i18nKey='app.purchase.wcw.detail.vehicle.position' />
            </Text>
          </th>
        </tr>
      </thead>
      <tbody>
        {compatibleVehicles.map((v) => (
          <tr key={`${v.brand}-${v.model}-${v.version}`}>
            <td>
              <Text weight='semi-bold' color='primary' heading='xxs'>
                {v.brand}
              </Text>
            </td>
            <td>
              <Text weight='semi-bold' color='primary' heading='xxs'>
                {v.model}
              </Text>
            </td>
            <td>
              <Text weight='semi-bold' color='primary' heading='xxs'>
                {v.version}
              </Text>
            </td>
            <td>
              <Text weight='semi-bold' color='primary' heading='xxs'>
                {[
                  t('app.purchase.wcw.detail.set_of', {
                    count: v.fitmentQuantity,
                  }),
                  v.fitmentQuantity === 2 ? v.position : undefined,
                ]
                  .filter(Boolean)
                  .join(' - ')}
              </Text>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const getter = {
  eco: (tyre: IWcwTyre) => tyre.specification.eco,
  sport: (tyre: IWcwTyre) => tyre.specification.sportCompatibility,
  electric: (tyre: IWcwTyre) => tyre.specification.electricId,
} as const;

function TyreInformationTabContent({ tyre }: { tyre: IWcwTyre }) {
  const { t } = useI18n();
  const specificities: string[] = [];

  for (const specificity of ['eco', 'sport', 'electric'] as const) {
    if (getter[specificity](tyre)) {
      specificities.push(t(getFilterTranslation(specificity)));
    }
  }

  return (
    <table className='table table-striped mt-3'>
      <tbody>
        <WheelRow labelKey='app.common.product.ean' value={tyre.ean} />
        <WheelRow
          labelKey='app.purchase.search_page.detail.reference_constructor'
          value={tyre.constructorReference}
        />
        <WheelRow
          labelKey='app.purchase.search_page.detail.cai'
          value={tyre.supplierCode}
        />
        <WheelRow
          labelKey='app.common.product.homologation'
          value={tyre.prodInfo}
        />

        <WheelRow
          labelKey='app.common.product.seal_run_flat'
          value={
            <Trans
              i18nKey={`app.common.messages.${
                tyre.specification.rftext || tyre.specification.seal === 'Y'
                  ? 'yes'
                  : 'no'
              }`}
            />
          }
        />
        <WheelRow
          labelKey='app.purchase.search_page.detail.specificities'
          value={
            specificities.length > 0 ? specificities.join(', ') : undefined
          }
        />
      </tbody>
    </table>
  );
}

const WcwDetailModal = ({ onClose, offer }: Props) => {
  const { cdnUrl, apiUrl } = useConfig();
  const { auth } = useApplicationContext();
  const [energyLabelSrc, setEnergyLabelSrc] = useState<string>(
    `data:image/png;base64,${DEFAULT_ENERGY_LABEL}`,
  );

  useEffect(() => {
    const controller = new AbortController();
    BlobHelpers.getDataUrl({
      url: `${apiUrl}/offers/api/assets/tyres/${offer.tyre.ean}/labels/medium`,
      token: auth.user?.access_token ?? '',
      signal: controller.signal,
    })
      .then((dataUrl) => {
        if (dataUrl) {
          setEnergyLabelSrc(dataUrl);
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [apiUrl, offer.tyre.ean, auth.user?.access_token]);

  return (
    <Modal.Container open={true} onClose={onClose} size='xl' centered>
      <Modal.Header />
      <Modal.Body>
        <Row className='ps-2'>
          <Col sm={5}>
            <ImageSlide
              images={[
                {
                  src: offer.wheel.image,
                  defaultImage: WcwSearchHelpers.DEFAULT_WHEEL_IMAGE,
                  baseUrl: cdnUrl,
                },
                {
                  src: energyLabelSrc,
                  defaultImage: `data:image/png;base64,${DEFAULT_ENERGY_LABEL}`,
                  baseUrl: undefined,
                },
              ]}
            />
          </Col>
          <Col>
            <Tooltip label={offer.tyre.brand.name}>
              <Img
                src={getBrandLogoUrl(offer.tyre.brand.name)}
                baseUrl={cdnUrl}
                defaultImage={DEFAULT_TIRE_BRAND_LOGO}
                alt={offer.tyre.brand.name}
                className='mb-2'
                width={DEFAULT_TIRE_BRAND_WIDTH.lg}
                loader={BrandLoader}
              />
            </Tooltip>
            <Title
              heading='lg'
              color='tertiary'
              weight='bold'
              className='text-uppercase'
            >
              {offer.wheel.name}
            </Title>
            <Flex className='gap-2 mt-2' alignItems='center'>
              <Badge pill bg='info'>
                <Text heading='xxs' weight='semi-bold' color='primary'>
                  {offer.tyre.dimension.fullSize}
                </Text>
              </Badge>
              <SeasonIcon value={offer.tyre.season.publicKey} size='xs' />
            </Flex>
            <Text heading='xxs' color='tertiary' className='mt-1'>
              {offer.tyre.brand.name} {offer.tyre.profile.design1}
            </Text>

            <div className='mt-3'>
              <Tabs.Container defaultActiveKey='wheel'>
                <Tabs.List>
                  <Tabs.Item eventKey='wheel' id='wheel'>
                    <Trans i18nKey='app.purchase.wcw.detail.wheel_information' />
                  </Tabs.Item>
                  <Tabs.Item eventKey='vehicle' id='vehicle'>
                    <Trans i18nKey='app.purchase.wcw.detail.vehicle_information' />
                  </Tabs.Item>
                  <Tabs.Item eventKey='tyre' id='tyre'>
                    <Trans i18nKey='app.purchase.wcw.detail.tyre_information' />
                  </Tabs.Item>
                </Tabs.List>
                <Tabs.Content>
                  <Tabs.Pane eventKey='wheel'>
                    <WheelInformationTabContent wheel={offer.wheel} />
                  </Tabs.Pane>
                  <Tabs.Pane eventKey='vehicle'>
                    <VehicleInformationTabContent
                      compatibleVehicles={offer.compatibleVehicles}
                    />
                  </Tabs.Pane>
                  <Tabs.Pane eventKey='tyre'>
                    <TyreInformationTabContent tyre={offer.tyre} />
                  </Tabs.Pane>
                </Tabs.Content>
              </Tabs.Container>
            </div>
          </Col>
        </Row>
      </Modal.Body>
    </Modal.Container>
  );
};

export default WcwDetailModal;
